<template>
  <SectionCard title="Screening on full text">
    <div class="flex flex-wrap gap-4 items-center">
      <div
        v-for="criterion in fullTextScreeingCriteria"
        :key="'full-text-criterion-' + criterion"
        class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-red-600 bg-red-50"
      >
        <span v-tooltip="criterion.length > 35 ? criterion : ''" class="truncate max-w-[252px]">{{
          criterion
        }}</span>
        <PlanLockGuard>
          <button
            :disabled="review.isPlanReadonly.value"
            @click="deleteFullTextCriterion(criterion)"
          >
            <XMarkIcon class="w-4 h-4" />
          </button>
        </PlanLockGuard>
      </div>
      <PlanLockGuard>
        <FloatingTextInput
          :disabled="review.isPlanReadonly.value"
          @save="addFullTextCriterion($event)"
        >
          <template #activator="{ show }">
            <button
              key=""
              class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
              :disabled="review.isPlanReadonly.value"
              @click.stop="show"
            >
              <PlusIcon class="w-4 h-4" /> Add criterion
            </button>
          </template>
        </FloatingTextInput>
      </PlanLockGuard>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import FloatingTextInput from "@app/components/Global/Inputs/FloatingTextInput.vue";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "@app/views/Review/use-review";
import { HttpException } from "@core/exceptions/http.exception";
import { ref, watchEffect } from "vue";
import PlusIcon from "@app/components/Icons/PlusIcon.vue";
import XMarkIcon from "@app/components/Icons/XMarkIcon.vue";
import SectionCard from "@app/components/Global/SectionCard.vue";
import PlanLockGuard from "../../PlanLockGuard.vue";

const loading = useLoading();
const snackbar = useSnackbar();

const review = injectStrict(ReviewKey);

const fullTextScreeingCriteria = ref<string[]>([]);
const titleAndAbstractScreeingCriteria = ref<string[]>([]);

watchEffect(() => {
  titleAndAbstractScreeingCriteria.value =
    review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria ?? [];

  fullTextScreeingCriteria.value = review.entity.value?.plan?.screeningPlan?.fullTextCriteria ?? [];
});

async function addFullTextCriterion(criterion: string) {
  loading.start();
  try {
    await review.addFullTextCriterion(criterion);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    if (error.response.data.statusCode >= 400 && error.response.data.statusCode < 500) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message);
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        "An error occured while adding full text criterion, please try again.",
      );
    }
    throw e;
  } finally {
    loading.stop();
  }
}

async function deleteFullTextCriterion(criterion: string) {
  loading.start();
  try {
    await review.deletFullTextCriterion(criterion);
    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    if (error.response.data.statusCode >= 400 && error.response.data.statusCode < 500) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message);
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        "An error occured while deleting full text criterion, please try again.",
      );
    }
    throw e;
  } finally {
    loading.stop();
  }
}
</script>
